import { call, put, all } from 'redux-saga/effects';
import moment from 'moment';

import { CourseTypes as Types } from '../ducks/course';
import { sagaErrorHandle } from './sagasError';
import {
  fetchCoursesList,
  fetchCourse,
  fetchNextLesson,
  fetchHtmlCertificate,
} from '../../services/course';
import { convertArrToObj, sortArrByKey, normalizeNumber } from '../../utils';

export function* getCoursesList() {
  yield all([
    put({ type: Types.SET_COURSE_LOADING, loadingType: 'list', loading: true }),
    put({ type: Types.SET_COURSE_ERROR, errorType: 'list', error: null }),
  ]);
  try {
    const { data: courses } = yield call(fetchCoursesList);
    if (courses.length) {
      const manipulator = course => ({
        ...course,
        courseDetailed: false,
        locked: moment(course.releaseDate).isAfter(moment(), 'days'),
      });
      const coursesObj = convertArrToObj(courses, 'id', manipulator);
      yield put({ type: Types.SET_COURSES_LIST, courses: coursesObj });
    } else {
      yield put({ type: Types.SET_COURSES_LIST, courses: {} });
    }
  } catch (error) {
    yield sagaErrorHandle({ sagaName: 'getCoursesList', errorData: error });
    yield put({
      type: Types.SET_COURSE_ERROR,
      errorType: 'list',
      error: 'Não foi possível carregar a listagem de cursos.',
    });
  } finally {
    yield put({
      type: Types.SET_COURSE_LOADING,
      loadingType: 'list',
      loading: false,
    });
  }
}

export function* getNextLesson(action) {
  const { courseId } = action;

  try {
    const { data: nextLesson } = yield call(fetchNextLesson, courseId);
    const normalizedNextLesson = nextLesson || { empty: true };
    yield put({
      type: Types.SET_NEXT_LESSON,
      courseId,
      nextLesson: {
        error: false,
        ...normalizedNextLesson,
      },
    });
  } catch (error) {
    yield sagaErrorHandle({
      sagaName: 'getNextLesson',
      errorData: error,
      additionalInfos: action,
    });
    yield put({
      type: Types.SET_NEXT_LESSON,
      courseId,
      nextLesson: { error: true },
    });
  }
}

export function* getCourse(action) {
  const { courseId } = action;

  yield all([
    put({
      type: Types.SET_COURSE_ERROR,
      errorType: 'current',
      error: null,
    }),
    put({
      type: Types.SET_COURSE_LOADING,
      loadingType: 'current',
      loading: true,
    }),
  ]);

  try {
    const { data: courseData } = yield call(fetchCourse, courseId);
    if (!courseData) {
      throw new Error(`Course not found`);
    }
    const arrModules = courseData.modules.map(m => ({ ...m, type: 'module' }));
    const modules = convertArrToObj(arrModules, 'id');
    const lessonsNormalized = courseData.lessons
      .filter(lesson => !lesson.daysToRelease || courseData.showBlockedLessons)
      .map(lesson => {
        const order = !lesson.moduleId
          ? lesson.order
          : Number(
              `${modules[lesson.moduleId].order}.${normalizeNumber(
                lesson.order,
                3,
              )}`,
            );
        return { ...lesson, order, type: 'lesson' };
      });
    const lessonsOrdered = sortArrByKey(lessonsNormalized, 'order');
    const locked = moment(courseData.releaseDate).isAfter(moment(), 'days');
    const lessons = convertArrToObj(lessonsOrdered, 'id');
    const completedAmmount = lessonsOrdered.filter(lesson => lesson.isConcluded)
      .length;
    const conclusionPercent = parseInt(
      (completedAmmount / lessonsOrdered.length) * 100,
      10,
    );
    const normalizedCertificate = (() => {
      if (!courseData.certificate) return false;
      const {
        daysToRelease: releaseDays,
        conclusionPercent: releasePercent,
      } = courseData.certificate;
      return {
        ...courseData.certificate,
        locked: !!releaseDays || releasePercent > conclusionPercent,
      };
    })();

    const courseObj = {
      ...courseData,
      conclusionPercent,
      modules,
      lessons,
      locked,
      certificate: normalizedCertificate,
      courseDetailed: true,
    };
    yield put({ type: Types.UPDATE_COURSES_LIST, course: courseObj });
  } catch (error) {
    let status;
    let msg;
    if (error.message !== 'Course not found') {
      const errorHandle = yield sagaErrorHandle({
        sagaName: 'getCourse',
        errorData: error,
        additionalInfos: action,
      });
      status = errorHandle.status;
      msg = errorHandle.message;
    }
    if (!status && !msg) {
      status = -1;
      msg = 'Application error on getCourse';
    }
    yield put({
      type: Types.SET_COURSE_ERROR,
      errorType: 'current',
      error: { status, msg },
    });
  } finally {
    yield put({
      type: Types.SET_COURSE_LOADING,
      loadingType: 'current',
      loading: false,
    });
  }
}

export function* getCertificateHtml(action) {
  const { courseId } = action;

  yield put({
    type: Types.SET_CERTIFICATE_HTML_ERROR,
    courseId,
    error: false,
  });

  try {
    const {
      data: { html },
    } = yield call(fetchHtmlCertificate, courseId);
    yield put({
      type: Types.SET_CERTIFICATE_HTML,
      courseId,
      html: html ?? '',
    });
  } catch (error) {
    let { status, message: msg } = yield sagaErrorHandle({
      sagaName: 'getCertificateHtml',
      errorData: error,
      additionalInfos: action,
    });
    if (!status && !msg) {
      status = -1;
      msg = 'Application error';
    }
    yield put({
      type: Types.SET_CERTIFICATE_HTML_ERROR,
      courseId,
      error: { status, msg },
    });
  }
}
